import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../lib/cart";
import { useSEO } from "../lib/seo";
import { Trash2 } from "lucide-react";
import BlurHashImage from "../components/BlurHashImage";

export default function Cart() {
  const { items, removeItem, updateItem, count } = useCart();
  const [offerQuote, setOfferQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!items.length) {
        setOfferQuote(null);
        setQuoteLoading(false);
        setQuoteError(false);
        return;
      }
      setQuoteLoading(true);
      setQuoteError(false);
      try {
        const base = process.env.REACT_APP_BACKEND_URL || "";
        const response = await fetch(`${base}/api/tax-quote`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ state: "Tamil Nadu", items }),
        });
        if (!response.ok) throw new Error("Could not calculate cart total");
        const quote = await response.json();
        if (!cancelled) setOfferQuote(quote);
      } catch {
        if (!cancelled) {
          setOfferQuote(null);
          setQuoteError(true);
        }
      } finally {
        if (!cancelled) setQuoteLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [items]);
  const cartTotal = items.reduce(
    (sum, item) =>
      sum +
      Number(
        item.total_price ??
          (item.price || 0) * (item.quantity || 1)
      ),
    0
  );

  const productDeliveryCharge = items.reduce(
    (highest, item) =>
      Math.max(
        highest,
        Number(item.delivery_charge || 0)
      ),
    0
  );
  const hasGift = items.some((item) => String(item.product_type || "").toLowerCase() === "gift");
  const continueShoppingPath = hasGift ? "/shop?category=Personalised%20Gifts" : "/shop";
  const sharedDeliveryCharge = productDeliveryCharge > 0 ? productDeliveryCharge : (hasGift ? 50 : 0);

  const offerDiscount = Number(offerQuote?.discount || 0);
  const quotedSubtotal = Number(offerQuote?.subtotal ?? cartTotal);
  const discountedCartTotal = Math.max(0, quotedSubtotal - offerDiscount);
  const shippingCharge = offerQuote
    ? Number(offerQuote.shipping_charge || 0)
    : (discountedCartTotal >= 1000 ? 0 : sharedDeliveryCharge);
  const displayCartTotal = Math.round(quotedSubtotal);
  const grandTotal = offerQuote
    ? Math.round(Number(offerQuote.grand_total ?? discountedCartTotal + shippingCharge))
    : Math.round(discountedCartTotal + shippingCharge);
  const freeDeliveryUnlocked = offerQuote
    ? Number(offerQuote.shipping_charge || 0) === 0
    : !quoteLoading && !quoteError && (discountedCartTotal >= 1000 || sharedDeliveryCharge <= 0);

  useSEO({
    title: "Your Cart — PrintMyTees",
    description:
      "Review items in your cart before checkout.",
  });

  return (
    <div
      className="container-pmt py-10 sm:py-12"
      data-testid="cart-page"
    >
      {/* =========================
          PAGE HEADER
      ========================== */}
      <div className="overline">
        Your Cart
      </div>

      <h1 className="display-h2 mt-2">
        {count > 0
          ? `${count} item${count > 1 ? "s" : ""} in cart`
          : "Cart is empty"}
      </h1>


      {items.length > 0 && (
        <div className="mt-5 grid grid-cols-3 gap-2 max-w-xl text-[10px] sm:text-xs uppercase tracking-wide">
          <div className="border border-[#D95328] bg-[#F3EDE2] px-2 py-2 text-center font-semibold text-[#D95328]">1. Cart</div>
          <div className="border border-[#E6DFD5] px-2 py-2 text-center text-stone-500">2. Details</div>
          <div className="border border-[#E6DFD5] px-2 py-2 text-center text-stone-500">3. Payment</div>
        </div>
      )}
      {/* =========================
          EMPTY CART
      ========================== */}
      {items.length === 0 && (
        <div
          className="mt-10 border border-dashed border-[#E6DFD5] p-10 text-center bg-white"
          data-testid="cart-empty"
        >
          <p className="text-stone-600">
            Add products or designs to see them here.
          </p>

          <Link
            to={continueShoppingPath}
            className="btn-primary mt-6 inline-flex"
            data-testid="cart-go-shop"
          >
            Browse Shop
          </Link>
          <Link to="/designs" className="btn-outline-pmt mt-3 sm:mt-6 sm:ml-3 inline-flex" data-testid="cart-go-designs">
            Browse Designs
          </Link>
        </div>
      )}

      {/* =========================
          CART
      ========================== */}
      {items.length > 0 && (
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
          {/* =========================
              CART ITEMS
          ========================== */}
          <div className="lg:col-span-2 space-y-4">
            {items.map((item) => {
              const quantity =
                item.quantity || 1;

              const unitPrice = Number(
                item.price || 0
              );

              const itemTotal = Number(
                item.total_price ??
                  unitPrice * quantity
              );

              return (
                <div
                  key={item._id}
                  className="card-pmt p-3 sm:p-5"
                  data-testid={`cart-item-${item._id}`}
                >
                  {/* Product information */}
                  <div className="min-w-0">
                    {item.image && (
                      <div className="mb-4 aspect-[4/3] sm:aspect-[16/9] bg-[#F3EDE2] overflow-hidden border border-[#E6DFD5]">
                        <BlurHashImage
                          src={item.image}
                          blurHash={item.blurhash}
                          alt={item.product_name || "Cart item"}
                          className="w-full h-full object-contain"
                        />
                      </div>
                    )}
                    <div
                      className="font-bold uppercase text-lg"
                      style={{
                        fontFamily:
                          "Barlow Condensed, sans-serif",
                      }}
                    >
                      {item.product_name}
                    </div>

                    {/* Design ID */}
                    {item.design_id && (
                      <div className="text-xs text-stone-500 mt-1">
                        Design ID:{" "}
                        {item.design_id}
                      </div>
                    )}

                    {/* Apparel attributes are not applicable to gifts */}
                    <div className="flex flex-wrap gap-2 mt-3">
                      {item.product_type !== "gift" && item.size && (
                        <span className="px-2.5 py-1 bg-[#F3EDE2] text-xs">
                          Size: {item.size}
                        </span>
                      )}

                      {item.product_type !== "gift" && item.color && (
                        <span className="px-2.5 py-1 bg-[#F3EDE2] text-xs">
                          Colour: {item.color}
                        </span>
                      )}

                      {item.product_type !== "gift" && item.quality && (
                        <span className="px-2.5 py-1 bg-[#F3EDE2] text-xs">
                          Quality: {item.quality}
                        </span>
                      )}

                      {item.product_type !== "gift" && item.printing_method && (
                        <span className="px-2.5 py-1 bg-[#F3EDE2] text-xs">
                          Print:{" "}
                          {item.printing_method}
                        </span>
                      )}

                      {item.product_type === "gift" && item.gift_options && Object.entries(item.gift_options).filter(([, value]) => value != null && String(value).trim() !== "").map(([key, value]) => (
                        <span key={key} className="px-2.5 py-1 bg-[#F3EDE2] text-xs">
                          {key.replace(/_/g, " ")}: {String(value)}
                        </span>
                      ))}

                      <span className="px-2.5 py-1 bg-[#F3EDE2] text-xs">
                        Qty: {quantity}
                      </span>
                    </div>

                    {/* Customisation details only for Shop orders */}
                    {(item.custom_text || item.custom_name || item.custom_number || item.notes) && (
                      <div className="mt-4 p-3 border border-[#E6DFD5] bg-[#F8F5F0] text-xs text-stone-600">
                        <div className="font-semibold text-stone-800 uppercase tracking-wide">Customisation</div>
                        <div className="mt-2 space-y-1">
                          {item.custom_name && <div><span className="font-medium text-stone-700">Name:</span> {item.custom_name}</div>}
                          {item.custom_number && <div><span className="font-medium text-stone-700">Number:</span> {item.custom_number}</div>}
                          {item.custom_text && <div><span className="font-medium text-stone-700">Text:</span> “{item.custom_text}”</div>}
                          {item.notes && <div><span className="font-medium text-stone-700">Instructions:</span> {item.notes}</div>}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Price + Quantity */}
                  <div className="mt-5 pt-4 border-t border-[#E6DFD5]">
                    <div className="flex justify-between items-end gap-4">
                      <div>
                        <div className="text-xs text-stone-500">
                          Unit price
                        </div>

                        <div className="font-mono text-sm mt-1">
                          ₹{Math.round(unitPrice).toLocaleString("en-IN")}
                        </div>
                      </div>

                      <div className="text-right">
                        <div className="text-xs text-stone-500">
                          Item total
                        </div>

                        <div className="font-semibold text-lg mt-1">
                          ₹{Math.round(itemTotal).toLocaleString("en-IN")}
                        </div>
                      </div>
                    </div>

                    {/* Quantity controls */}
                    <div className="flex items-center justify-between mt-4">
                      <div className="flex items-center border border-[#E6DFD5]">
                        <button
                          type="button"
                          className="w-9 h-9 hover:bg-[#F3EDE2] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D95328]" aria-label="Decrease quantity"
                          onClick={() =>
                            updateItem(
                              item._id,
                              {
                                quantity:
                                  Math.max(
                                    1,
                                    quantity - 1
                                  ),
                              }
                            )
                          }
                          data-testid={`cart-dec-${item._id}`}
                        >
                          −
                        </button>

                        <span className="w-10 text-center text-sm">
                          {quantity}
                        </span>

                        <button
                          type="button"
                          onClick={() =>
                            updateItem(
                              item._id,
                              {
                                quantity:
                                  Math.min(1000, quantity + 1),
                              }
                            )
                          }
                          disabled={quantity >= 1000}
                          className="w-9 h-9 hover:bg-[#F3EDE2] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D95328] disabled:opacity-40 disabled:cursor-not-allowed"
                          aria-label="Increase quantity"
                          data-testid={`cart-inc-${item._id}`}
                        >
                          +
                        </button>
                      </div>

                      <button
                        type="button"
                        onClick={() =>
                          removeItem(item._id)
                        }
                        className="inline-flex items-center gap-1.5 px-2 py-2 text-xs text-stone-500 hover:text-[#D95328] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D95328]"
                        aria-label={`Remove ${item.product_name}`}
                        data-testid={`cart-remove-${item._id}`}
                      >
                        <Trash2 size={16} aria-hidden="true" /><span>Remove</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* =========================
              ORDER SUMMARY
          ========================== */}
          <aside
            className="lg:col-span-3 card-pmt p-4 sm:p-7 h-fit lg:sticky lg:top-24"
            data-testid="cart-summary"
          >
            <div className="flex items-center justify-between">
              <div className="overline">
                Order Summary
              </div>

              <div className="text-xs uppercase text-stone-500">
                {count}{" "}
                {count === 1
                  ? "Item"
                  : "Items"}
              </div>
            </div>

            {/* Summary items */}
            <div className="mt-5 space-y-4 max-h-[420px] overflow-auto pr-1">
              {items.map((item) => {
                const quantity =
                  item.quantity || 1;

                const unitPrice = Number(
                  item.price || 0
                );

                const itemTotal = Number(
                  item.total_price ??
                    unitPrice * quantity
                );

                return (
                  <div
                    key={item._id}
                    className="border-b border-[#E6DFD5] pb-4"
                  >
                    <div className="flex justify-between gap-4">
                      <div className="min-w-0">
                        <div className="font-semibold text-base">
                          {item.product_name}
                        </div>

                        {item.design_id && (
                          <div className="text-xs text-stone-500 mt-1">
                            Design ID:{" "}
                            {item.design_id}
                          </div>
                        )}

                        <div className="flex flex-wrap gap-2 mt-3">
                          {item.product_type !== "gift" && item.size && (
                            <span className="px-2.5 py-1 bg-[#F3EDE2] text-xs">
                              Size:{" "}
                              {item.size}
                            </span>
                          )}

                          {item.product_type !== "gift" && item.color && (
                            <span className="px-2.5 py-1 bg-[#F3EDE2] text-xs">
                              Colour:{" "}
                              {item.color}
                            </span>
                          )}

                          {item.product_type === "gift" && item.gift_options && Object.entries(item.gift_options).filter(([, value]) => value != null && String(value).trim() !== "").map(([key, value]) => (
                            <span key={key} className="px-2.5 py-1 bg-[#F3EDE2] text-xs">
                              {key.replace(/_/g, " ")}: {String(value)}
                            </span>
                          ))}

                          <span className="px-2.5 py-1 bg-[#F3EDE2] text-xs">
                            Qty: {quantity}
                          </span>
                        </div>
                      </div>

                      <div className="text-right flex-shrink-0">
                        <div className="font-mono text-sm">
                          ₹{Math.round(unitPrice).toLocaleString("en-IN")}
                        </div>

                        <div className="text-[11px] text-stone-500">
                          each
                        </div>

                        <div className="font-semibold mt-2">
                          ₹{Math.round(itemTotal).toLocaleString("en-IN")}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {discountedCartTotal < 1000 && sharedDeliveryCharge > 0 && !offerQuote?.offer_free_delivery && !freeDeliveryUnlocked && (
              <div className="mb-4 p-3 text-sm border border-[#E6DFD5] bg-[#F3EDE2] text-[#3B3B3B]">
                🚚 Add ₹{Math.max(1, Math.ceil(1000 - discountedCartTotal)).toLocaleString("en-IN")} more to get FREE DELIVERY!
              </div>
            )}

            {freeDeliveryUnlocked && (
              <div className="mb-4 p-3 text-sm border border-green-200 bg-green-50 text-green-700">
                🎉 You’ve unlocked FREE DELIVERY!
              </div>
            )}

            {offerQuote?.offer_name && offerDiscount > 0 && <div className="mb-4 p-3 text-sm border border-green-200 bg-green-50 text-green-700">🎉 {offerQuote.offer_name} — Save ₹{Math.round(offerDiscount).toLocaleString("en-IN")}</div>}
            {offerQuote?.offer_message && offerDiscount <= 0 && <div className="mb-4 p-3 text-sm border border-amber-200 bg-amber-50 text-amber-800">{offerQuote.offer_message}</div>}
            {quoteLoading && <p className="mb-3 text-xs text-stone-500" role="status">Updating your total…</p>}
            {quoteError && <p className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs leading-5 text-amber-800" role="status">We couldn’t verify the latest discount, shipping and tax estimate. Please continue to checkout to refresh the official total before paying.</p>}
            {/* Price breakdown */}
            <div className="mt-5 space-y-3">
              <div className="flex justify-between text-sm">
                <span>
                  Product Subtotal ({count}{" "}
                  {count === 1
                    ? "item"
                    : "items"})
                </span>

                <span className="font-mono">
                  ₹{displayCartTotal.toLocaleString("en-IN")}
                </span>
              </div>

              {offerDiscount > 0 && <div className="flex justify-between text-sm text-green-700"><span>Offer Discount</span><span>−₹{Math.round(offerDiscount).toLocaleString("en-IN")}</span></div>}

              <div className="flex justify-between text-sm">
                <span>Shipping</span>

                <span className="font-semibold text-green-600">
                    {shippingCharge <= 0
                      ? "FREE DELIVERY"
                      : `₹${Math.round(shippingCharge).toLocaleString("en-IN")}`}
                  </span>
              </div>              {Number(offerQuote?.gst_amount || 0) > 0 && (
                <div className="flex justify-between text-sm">
                  <span>GST</span>
                  <span className="font-mono">₹{Math.round(Number(offerQuote.gst_amount)).toLocaleString("en-IN")}</span>
                </div>
              )}

              {/* Total */}
              <div className="mt-4 rounded-xl p-3 sm:p-4 bg-[#F3EDE2] border border-[#E6DFD5]">
                <div className="flex justify-between items-center">
                  <span className="font-semibold">
                    Grand Total
                  </span>

                  <span className="font-mono text-lg sm:text-xl font-bold text-[#D95328]">
                    ₹{grandTotal.toLocaleString("en-IN")}
                  </span>
                </div>
                <p className="mt-2 text-[11px] leading-5 text-stone-600">Estimated cart total. Your final payable amount, including any applicable taxes, is confirmed at checkout.</p>
              </div>
            </div>

            {/* Checkout */}
            <Link
              to="/checkout"
              className="btn-primary w-full mt-5 min-h-12 py-3 sm:py-4"
              data-testid="cart-checkout"
            >
              Proceed to Checkout
            </Link>

            {/* Continue shopping */}
            <Link
              to={continueShoppingPath}
              className="block text-xs text-stone-500 mt-4 underline text-center"
              data-testid="cart-continue"
            >
              Continue Shopping
            </Link>

            <div className="mt-4 rounded-xl border border-[#E6DFD5] bg-white p-4 text-xs text-stone-600" data-testid="cart-trust-panel">
              <div className="font-bold text-[#171615]">Questions before checkout?</div>
              <p className="mt-1 leading-5">Check delivery and return details, or ask us about your order before continuing.</p>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 font-semibold">
                <Link to="/shipping-policy" className="underline underline-offset-4 hover:text-[#D95328]">Shipping policy</Link>
                <Link to="/return-refund-policy" className="underline underline-offset-4 hover:text-[#D95328]">Returns & refunds</Link>
                <a href="https://wa.me/919566853582" target="_blank" rel="noreferrer" className="underline underline-offset-4 hover:text-[#D95328]">Chat on WhatsApp</a>
              </div>
              <a href="mailto:support@printmytees.in" className="mt-3 inline-block text-stone-500 underline underline-offset-4 hover:text-[#D95328]">support@printmytees.in</a>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}